import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { FileNode, DocumentStatus } from '../types';
import apiService from '../services/api';

const DEFAULT_EXPANDED_FOLDER_IDS = ['org-root', 'private-vault', 'recycle-bin'];

/** One in-flight (or just-finished) document upload, tracked app-wide so the
    progress card, completion toast and the chat "ready" message behave the
    same whether the file came from the 📎 attach button, drag-and-drop or the
    TASE import modal. */
export interface UploadTracker {
  documentId: number;
  filename: string;
  status: string;
  progress: number;
  stage: string;
  error?: string;
  // Set to Date.now() at the moment the status first transitions to PROCESSED
  // (never overwritten afterwards). The minimum-dwell is measured from THIS,
  // not from upload start — so a just-completed card shows a visible
  // "מעבד… → מוכן" beat regardless of how long extraction took.
  processedAt?: number;
  // Card hidden (dwell elapsed / user closed it). The entry is kept — not
  // deleted — so filename lookups (scope chip, message labels) keep resolving
  // for docs the tree hasn't returned yet.
  dismissed?: boolean;
}

/** Minimum time a completed upload card stays on screen before auto-fading —
    guarantees a visible "מעבד… → מוכן" beat even for sub-second extractions. */
export const UPLOAD_DONE_DWELL_MS = 1400;

interface FileTreeContextType {
  tree: FileNode[];
  expandedIds: Set<string>;
  selectedId: string | null;
  selectedFileIds: Set<string>;
  /** Documents attached to the *current chat* (e.g. uploaded in-composer).
      Unlike `selectedFileIds` (ad-hoc, one-shot tree picks cleared after each
      send), these persist across turns and are shown in the Sources panel until
      the chat changes or the user removes them. */
  sessionSourceIds: Set<string>;
  isLoading: boolean;
  loadError: string | null;
  setTree: React.Dispatch<React.SetStateAction<FileNode[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  toggleExpanded: (id: string) => void;
  setSelectedId: (id: string | null) => void;
  toggleFileSelection: (id: string) => void;
  clearFileSelection: () => void;
  addSessionSource: (id: string) => void;
  removeSessionSource: (id: string) => void;
  /** Replace the whole set of chat sources at once — used when (re)loading a
      session so its persisted document context is restored verbatim. */
  setSessionSources: (ids: Iterable<string>) => void;
  clearSessionSources: () => void;
  expandAll: () => void;
  collapseAll: () => void;
  refreshTree: () => Promise<void>;
  refreshTreeWithVault: () => Promise<void>;
  /** Uploads currently processing (or briefly post-completion). Polled here so
      progress + "ready" notifications work from every upload entry point. */
  trackedUploads: UploadTracker[];
  trackUpload: (u: { documentId: number; filename: string; status?: string; stage?: string }) => void;
  dismissUpload: (documentId: number) => void;
}

const FileTreeContext = createContext<FileTreeContextType | undefined>(undefined);

function collectAllFolderIds(nodes: FileNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.type === 'folder') {
      ids.push(node.id);
      if (node.children) {
        ids.push(...collectAllFolderIds(node.children));
      }
    }
  }
  return ids;
}

export const FileTreeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tree, setTree] = useState<FileNode[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(DEFAULT_EXPANDED_FOLDER_IDS));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [sessionSourceIds, setSessionSourceIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const refreshInFlightRef = useRef(false);
  const refreshQueuedRef = useRef(false);
  const retryAttemptsRef = useRef(0);
  const lastRefreshAtRef = useRef(0);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleFileSelection = useCallback((id: string) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearFileSelection = useCallback(() => {
    setSelectedFileIds(new Set());
  }, []);

  const addSessionSource = useCallback((id: string) => {
    setSessionSourceIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const removeSessionSource = useCallback((id: string) => {
    setSessionSourceIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const setSessionSources = useCallback((ids: Iterable<string>) => {
    setSessionSourceIds(new Set(ids));
  }, []);

  const clearSessionSources = useCallback(() => {
    setSessionSourceIds(new Set());
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(collectAllFolderIds(tree)));
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const refreshTree = useCallback(async (includeVault = false) => {
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return;
    }
    refreshInFlightRef.current = true;
    lastRefreshAtRef.current = Date.now();
    setIsLoading(true);
    try {
      do {
        refreshQueuedRef.current = false;
        const data = await apiService.getFolderTree({ includeVault });
        setTree(data);
        setLoadError(null);
        setHasLoadedOnce(true);
        retryAttemptsRef.current = 0;
        setExpandedIds((prev) => {
          const next = new Set(prev);
          for (const id of DEFAULT_EXPANDED_FOLDER_IDS) {
            next.add(id);
          }
          return next;
        });
      } while (refreshQueuedRef.current);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setLoadError(typeof detail === 'string' ? detail : 'טעינת הקבצים נכשלה');
    } finally {
      refreshInFlightRef.current = false;
      setIsLoading(false);
    }
  }, []);

  const refreshTreeWithVault = useCallback(() => refreshTree(true), [refreshTree]);

  // ── Upload tracking ──────────────────────────────────────────────────────
  const [trackedUploads, setTrackedUploads] = useState<UploadTracker[]>([]);

  const trackUpload = useCallback(
    ({ documentId, filename, status = DocumentStatus.UPLOADED, stage = '' }: {
      documentId: number;
      filename: string;
      status?: string;
      stage?: string;
    }) => {
      setTrackedUploads((prev) => [
        {
          documentId,
          filename,
          status,
          progress: 5,
          stage,
          // A doc that comes back already processed (fast digital PDF) never
          // goes through the poll transition — stamp its dwell start here.
          processedAt: status === DocumentStatus.PROCESSED ? Date.now() : undefined,
        },
        ...prev.filter((item) => item.documentId !== documentId),
      ]);
    },
    [],
  );

  const dismissUpload = useCallback((documentId: number) => {
    setTrackedUploads((prev) =>
      prev.map((item) => (item.documentId === documentId ? { ...item, dismissed: true } : item)),
    );
  }, []);

  // Poll processing documents every 3s. On completion: refresh the tree once
  // and toast — the toast is the "ready" signal that reaches the user on every
  // route (the chat additionally posts an in-conversation message).
  useEffect(() => {
    const activeUploads = trackedUploads.filter(
      (upload) => upload.status === DocumentStatus.UPLOADED || upload.status === DocumentStatus.PROCESSING,
    );
    if (activeUploads.length === 0) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const nextStatuses = await Promise.all(
          activeUploads.map(async (upload) => {
            try {
              const status = await apiService.getDocumentStatus(upload.documentId);
              const newStatus = String(status.status || upload.status);
              return {
                documentId: upload.documentId,
                filename: upload.filename,
                status: newStatus,
                progress: typeof status.progress === 'number'
                  ? Math.max(0, Math.min(100, status.progress))
                  : upload.progress,
                stage: status.stage || status.message || upload.stage,
                error: status.error || undefined,
                // Stamp processedAt exactly once — when we first observe
                // PROCESSED — so the dwell shows for slow extractions too.
                processedAt:
                  upload.processedAt
                  ?? (newStatus === DocumentStatus.PROCESSED ? Date.now() : undefined),
              } satisfies UploadTracker;
            } catch (error) {
              console.error('Upload status polling failed', error);
              return upload;
            }
          }),
        );

        if (cancelled) return;

        const completed = nextStatuses.filter((next) => {
          const prev = activeUploads.find((u) => u.documentId === next.documentId);
          return prev && prev.status !== next.status &&
            (next.status === DocumentStatus.PROCESSED || next.status === DocumentStatus.FAILED);
        });

        setTrackedUploads((prev) =>
          prev.map((upload) => nextStatuses.find((item) => item.documentId === upload.documentId) || upload),
        );

        for (const done of completed) {
          if (done.status === DocumentStatus.PROCESSED) {
            toast.success(`"${done.filename}" מוכן — אפשר לשאול עליו בצ׳אט`);
          } else {
            toast.error(`עיבוד "${done.filename}" נכשל`);
          }
        }

        // Only refresh tree when an upload finishes — not on every poll.
        if (completed.length > 0) {
          refreshTree();
        }
      } catch (error) {
        console.error('Upload tracker polling failed', error);
      }
    };

    poll();
    const intervalId = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [trackedUploads, refreshTree]);

  // Auto-dismiss a *completed* (PROCESSED) upload card after its minimum
  // dwell, measured from processedAt. FAILED cards are left in place — the
  // user must see and act on them; they dismiss manually. Timers are kept
  // per-documentId so each card is scheduled exactly once.
  const dismissTimers = useRef<Map<number, number>>(new Map());
  useEffect(() => {
    const timers = dismissTimers.current;
    const stillProcessed = new Set<number>();
    for (const u of trackedUploads) {
      if (u.status !== DocumentStatus.PROCESSED || u.processedAt == null || u.dismissed) continue;
      stillProcessed.add(u.documentId);
      if (timers.has(u.documentId)) continue;
      const delay = Math.max(0, u.processedAt + UPLOAD_DONE_DWELL_MS - Date.now());
      const tid = window.setTimeout(() => {
        timers.delete(u.documentId);
        setTrackedUploads((prev) =>
          prev.map((item) =>
            item.documentId === u.documentId ? { ...item, dismissed: true } : item,
          ),
        );
      }, delay);
      timers.set(u.documentId, tid);
    }
    // Drop timers for uploads that vanished or are no longer PROCESSED.
    timers.forEach((tid, docId) => {
      if (!stillProcessed.has(docId)) {
        window.clearTimeout(tid);
        timers.delete(docId);
      }
    });
  }, [trackedUploads]);
  useEffect(() => {
    const timers = dismissTimers.current;
    return () => {
      timers.forEach((tid) => window.clearTimeout(tid));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    // visibilitychange covers tab switches; focus covers multi-window setups
    // where the tab stays "visible" but the window loses focus. Both can fire
    // back-to-back when alt-tabbing, so the 2s dedupe keeps it to one refresh.
    const handleVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAtRef.current < 2000) return;
      refreshTree();
    };

    window.addEventListener('focus', handleVisible);
    document.addEventListener('visibilitychange', handleVisible);
    return () => {
      window.removeEventListener('focus', handleVisible);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [refreshTree]);

  // REMOVED: tree auto-refresh during document processing.
  // Upload progress is tracked in ChatArea via /documents/{id}/status polling.
  // Tree refreshes only when an upload finishes (ChatArea.tsx justCompleted check).
  // The old 3-second full-tree refresh caused vault retries + UI flicker.

  useEffect(() => {
    const shouldRetry = Boolean(loadError) || (!hasLoadedOnce && tree.length === 0);
    if (!shouldRetry) return;

    // Exponential backoff capped at 60s: 5s → 10s → 20s → 40s → 60s → 60s …
    // Prevents hammering a flapping API. retryAttemptsRef resets to 0 on
    // a successful refresh (see refreshTree above).
    const delay = Math.min(5000 * 2 ** retryAttemptsRef.current, 60000);
    const timeoutId = window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        retryAttemptsRef.current += 1;
        refreshTree();
      }
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [hasLoadedOnce, loadError, tree.length, refreshTree]);

  return (
    <FileTreeContext.Provider
      value={{
        tree,
        expandedIds,
        selectedId,
        selectedFileIds,
        sessionSourceIds,
        isLoading,
        loadError,
        setTree,
        setIsLoading,
        toggleExpanded,
        setSelectedId,
        toggleFileSelection,
        clearFileSelection,
        addSessionSource,
        removeSessionSource,
        setSessionSources,
        clearSessionSources,
        expandAll,
        collapseAll,
        refreshTree,
        refreshTreeWithVault,
        trackedUploads,
        trackUpload,
        dismissUpload,
      }}
    >
      {children}
    </FileTreeContext.Provider>
  );
};

export const useFileTreeContext = () => {
  const context = useContext(FileTreeContext);
  if (!context) {
    throw new Error('useFileTreeContext must be used within a FileTreeProvider');
  }
  return context;
};
